// ===================================================================
// MarketForge — Strategy Verdicts Section (extracted Iter118).
//
// Renders read-only per-strategy verdicts (BPCS, BCCS, Iron Condor,
// CFM Shares, CFM Leaps) for the current symbol below the GEX
// Advanced View. Same data path the Strategy Scanner page uses —
// POST /api/forge/toolbox/scanner/unified-preview with one symbol.
//
// Strict invariants (matching the rest of the cockpit):
//   • No backend writes, no journal mutation.
//   • Read-only display; no broker / pin actions here.
//   • Re-fetch is debounced to 1 request per symbol change.
//   • Anti-flicker via dataSymbolRef — only nuke rows when the symbol
//     actually changes; same-symbol refresh keeps prior data painted.
// ===================================================================

import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Layers, Loader2, AlertTriangle, CheckCircle2, ShieldAlert, ChevronDown, Copy, Check } from "lucide-react";
import FreshnessIndicator from "@/components/FreshnessIndicator";
import InlineError from "@/components/InlineError";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STRATEGY_VERDICT_MODULES = [
  { id: "BPCS",        title: "Bull Put Credit Spread" },
  { id: "BCCS",        title: "Bear Call Credit Spread" },
  { id: "IRON_CONDOR", title: "Iron Condor" },
  { id: "CFM_SHARES",  title: "Covered Call · Shares" },
  { id: "CFM_LEAPS",   title: "Covered Call · LEAPS / PMCC" },
];

const STRATEGY_STATUS_STYLES = {
  ENTER:           { bg: "bg-[#00FF88]/10", border: "border-[#00FF88]/40", text: "text-[#00FF88]", IconCmp: CheckCircle2 },
  WATCH:           { bg: "bg-[#FFB800]/10", border: "border-[#FFB800]/40", text: "text-[#FFB800]", IconCmp: AlertTriangle },
  NO_TRADE:        { bg: "bg-[#5F5F66]/10", border: "border-[#5F5F66]/40", text: "text-[#8A8A93]", IconCmp: ShieldAlert },
  BLOCKED:         { bg: "bg-[#FF3366]/10", border: "border-[#FF3366]/40", text: "text-[#FF3366]", IconCmp: ShieldAlert },
  CONTEXT_MISSING: { bg: "bg-[#22D3FF]/10", border: "border-[#22D3FF]/40", text: "text-[#22D3FF]", IconCmp: AlertTriangle },
  ERROR:           { bg: "bg-[#FF3366]/10", border: "border-[#FF3366]/40", text: "text-[#FF3366]", IconCmp: ShieldAlert },
};

function formatModuleLabel(moduleId, t) {
  // Friendly labels for the chips (i18n-free fallback strings).
  switch (moduleId) {
    case "BPCS":        return t("scanner.bpcs_short") || "BPCS";
    case "BCCS":        return t("scanner.bccs_short") || "BCCS";
    case "IRON_CONDOR": return t("scanner.iron_condor_short") || "Iron Condor";
    case "CFM_SHARES":  return t("scanner.cfm_shares_short") || "CC · Shares";
    case "CFM_LEAPS":   return t("scanner.cfm_leaps_short") || "CC · LEAPS";
    default:            return moduleId;
  }
}

// Iter118.1 — Clipboard helper shared by the per-card and "Copy all"
// affordances. Falls back to a hidden textarea when navigator.clipboard
// is unavailable (older browsers, non-secure contexts).
async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand("copy"); } catch { /* swallow */ }
    document.body.removeChild(ta);
  }
}

// Iter118.1 — One-line summary builder. Same shape the TradeVerdictCard
// uses (symbol · status · confidence · risk · topReason) so operators
// get consistent copy output across the cockpit.
function buildVerdictLine(row, symbol, t) {
  const parts = [];
  parts.push(String(symbol || row?.symbol || "—").toUpperCase());
  parts.push(formatModuleLabel(row?.module_id, t));
  parts.push(row?.status || "—");
  if (row?.confidence_score != null) parts.push(`Conf ${row.confidence_score}/100`);
  if (row?.risk_score != null) parts.push(`Risk ${row.risk_score}/100`);
  const topReason = row?.reason_codes?.[0];
  if (topReason) parts.push(topReason);
  return parts.join(" · ");
}

function StrategyVerdictMiniCard({ row, title, symbol, t }) {
  const status = row?.status || "CONTEXT_MISSING";
  const styles = STRATEGY_STATUS_STYLES[status] || STRATEGY_STATUS_STYLES.CONTEXT_MISSING;
  const StatusIcon = styles.IconCmp;
  const conf = row?.confidence_score ?? 0;
  const topReason = (row?.reason_codes && row.reason_codes[0]) || null;
  const [open, setOpen] = useState(false);
  // Iter118.1 — Per-card copy-to-clipboard affordance. 1.8 s ephemeral
  // confirmation, mirrors the TradeVerdictCard pattern from Iter116.1.
  const [copied, setCopied] = useState(false);
  const handleCopy = async (e) => {
    e.stopPropagation();
    await copyToClipboard(buildVerdictLine(row, symbol, t));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div data-testid={`forge-verdict-mini-${row?.module_id || "unknown"}`}
         className={`bg-[#121216] border border-[#26262B] rounded-sm p-3 transition-colors hover:border-[#3F3F47]`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <StatusIcon size={12} className={styles.text} />
          <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-white truncate" title={title}>
            {formatModuleLabel(row?.module_id, t)}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className={`text-[8px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm border ${styles.border} ${styles.text} ${styles.bg}`}>
            {status}
          </span>
          <button
            type="button"
            data-testid={`forge-verdict-mini-${row?.module_id || "unknown"}-copy`}
            onClick={handleCopy}
            title="Copy this verdict (symbol · module · status · confidence · risk · reason)"
            aria-label="Copy verdict line"
            className={`flex items-center justify-center w-5 h-5 rounded-sm border transition-colors ${
              copied
                ? "border-[#00FF88]/60 text-[#00FF88] bg-[#00FF88]/10"
                : "border-[#26262B] text-[#5F5F66] hover:text-white hover:border-[#3F3F47]"
            }`}
          >
            {copied ? <Check size={10} /> : <Copy size={10} />}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] font-mono mb-2">
        <span className="text-[#8A8A93]">Confidence</span>
        <span className={`text-right font-bold ${conf >= 70 ? "text-[#00FF88]" : conf >= 40 ? "text-[#FFB800]" : "text-[#8A8A93]"}`}>
          {conf}/100
        </span>
        {row?.risk_score != null && (
          <>
            <span className="text-[#8A8A93]">Risk</span>
            <span className={`text-right font-bold ${row.risk_score >= 70 ? "text-[#FF3366]" : row.risk_score >= 40 ? "text-[#FFB800]" : "text-[#8A8A93]"}`}>
              {row.risk_score}/100
            </span>
          </>
        )}
      </div>
      {topReason && (
        <div className="text-[9px] font-mono text-[#A0A0A8] italic leading-snug truncate" title={topReason}>
          {topReason}
        </div>
      )}
      {(row?.reason_codes?.length > 1 || row?.selected_structure || row?.errors?.length > 0) && (
        <button
          type="button"
          data-testid={`forge-verdict-mini-${row?.module_id}-expand`}
          onClick={() => setOpen(o => !o)}
          className="mt-2 w-full flex items-center justify-center gap-1 text-[8px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] hover:text-white border border-[#26262B] hover:border-[#3F3F47] px-2 py-1 rounded-sm"
        >
          {open ? <ChevronDown size={9} className="rotate-180 transition-transform" /> : <ChevronDown size={9} className="transition-transform" />}
          {open ? "Hide details" : "View details"}
        </button>
      )}
      {open && (
        <div className="mt-2 space-y-1.5 text-[9px] font-mono text-[#A0A0A8]">
          {row.reason_codes?.length > 1 && (
            <div>
              <div className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#5F5F66] mb-0.5">Reasons</div>
              <ul className="list-disc pl-3 space-y-0.5 leading-snug">
                {row.reason_codes.map((rc, i) => <li key={i}>{rc}</li>)}
              </ul>
            </div>
          )}
          {row.selected_structure && (
            <div>
              <div className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#5F5F66] mb-0.5">Selected structure</div>
              <pre className="bg-[#08080A] border border-[#26262B] rounded-sm px-1.5 py-1 overflow-x-auto max-h-32 text-[8px] leading-relaxed text-[#E4E4E7]">
{JSON.stringify(row.selected_structure, null, 2)}
              </pre>
            </div>
          )}
          {row.errors?.length > 0 && (
            <div>
              <div className="text-[8px] font-bold uppercase tracking-[0.15em] text-[#FF3366] mb-0.5">Errors</div>
              <ul className="list-disc pl-3 space-y-0.5 leading-snug text-[#FF3366]">
                {row.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function StrategyVerdictsSection({ symbol, timeframe, t }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [ctx, setCtx] = useState(null);
  // Iter117.2 — Freshness tracker delegated to the shared
  // <FreshnessIndicator /> component so other tables can adopt the
  // exact same dot + ago + Refresh UX with one import.
  const [fetchedAt, setFetchedAt] = useState(null);
  // Iter118.1 — "Copy all verdicts" affordance. Lets operators paste
  // all 5 module verdicts as a multi-line block into Discord / notes
  // / chat without taking a screenshot. 1.8 s ephemeral confirmation.
  const [copiedAll, setCopiedAll] = useState(false);

  // Iter117.8 — Anti-flicker: only wipe rows + context when the
  // SYMBOL actually changes. Refresh button and timeframe change
  // keep the previous verdicts painted while new ones arrive.
  const dataSymbolRef = useRef(null);

  const loadVerdicts = useCallback(() => {
    if (!symbol) return () => {};
    let alive = true;
    const symbolChanged = symbol !== dataSymbolRef.current;
    if (symbolChanged) {
      setRows(null);
      setErr(null);
      setCtx(null);
    }
    setLoading(true);
    axios.post(`${API}/forge/toolbox/scanner/unified-preview`, {
      symbols: [symbol],
      context_mode: "live",
      modules: {},  // empty = all modules enabled
    })
      .then(r => {
        if (!alive) return;
        const data = r.data || {};
        setRows(Array.isArray(data.rows) ? data.rows : []);
        if (Array.isArray(data.rows) && data.rows[0]?.context_metrics) {
          setCtx(data.rows[0].context_metrics);
        }
        setFetchedAt(Date.now());
        setErr(null);
        dataSymbolRef.current = symbol;
      })
      .catch(e => { if (alive) setErr(e?.response?.data?.detail || e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [symbol]);

  // Auto-refresh on symbol OR timeframe change.
  useEffect(() => {
    return loadVerdicts();
  }, [loadVerdicts, timeframe]);

  const rowsByModule = (rows || []).reduce((acc, r) => {
    acc[r.module_id] = r;
    return acc;
  }, {});

  // Iter118.1 — Multi-line block for "Copy all". Preserves the module
  // ordering defined in STRATEGY_VERDICT_MODULES (BPCS → BCCS → IC →
  // CFM_SHARES → CFM_LEAPS) so chat output reads predictably.
  const handleCopyAll = async () => {
    const header = `Strategy Verdicts · ${(symbol || "—").toUpperCase()}`;
    const ctxLine = ctx
      ? [
          ctx.spot != null ? `Spot $${Number(ctx.spot).toFixed(2)}` : null,
          ctx.dte != null ? `DTE ${ctx.dte}d` : null,
          ctx.ivr != null ? `IVR ${Number(ctx.ivr).toFixed(0)}` : null,
          ctx.regime ? `Regime ${ctx.regime}` : null,
        ].filter(Boolean).join(" · ")
      : null;
    const lines = STRATEGY_VERDICT_MODULES.map(m => {
      const row = rowsByModule[m.id] || { module_id: m.id, status: "CONTEXT_MISSING" };
      return "  • " + buildVerdictLine(row, symbol, t);
    });
    const block = [header, ctxLine ? `  ${ctxLine}` : null, ...lines]
      .filter(Boolean)
      .join("\n");
    await copyToClipboard(block);
    setCopiedAll(true);
    window.setTimeout(() => setCopiedAll(false), 1800);
  };
  const canCopyAll = Array.isArray(rows) && rows.length > 0;

  return (
    <div data-testid="forge-strategy-verdicts-section"
         className="bg-[#121216] border border-[#26262B] rounded-sm p-4">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Layers size={14} className="text-[#00FF88]" />
          <h3 className="text-[11px] font-bold uppercase tracking-[0.18em] text-white">
            Strategy Verdicts · {symbol}
          </h3>
          <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#5F5F66]">
            read-only · all modules
          </span>
          {loading && <Loader2 size={11} className="animate-spin text-[#8A8A93]" />}
        </div>
        <div className="flex items-center gap-3">
          {ctx && (
            <div className="flex items-center gap-3 text-[9px] font-mono text-[#8A8A93]">
              {ctx.spot != null && <span>Spot <span className="text-white font-bold">${Number(ctx.spot).toFixed(2)}</span></span>}
              {ctx.dte != null && <span>DTE <span className="text-[#22D3FF] font-bold">{ctx.dte}d</span></span>}
              {ctx.ivr != null && <span>IVR <span className={`font-bold ${ctx.ivr >= 50 ? "text-[#00FF88]" : "text-[#FFB800]"}`}>{Number(ctx.ivr).toFixed(0)}</span></span>}
              {ctx.regime && <span>Regime <span className="text-white font-bold">{ctx.regime}</span></span>}
            </div>
          )}
          <button
            type="button"
            data-testid="forge-strategy-verdicts-copy-all"
            onClick={handleCopyAll}
            disabled={!canCopyAll}
            title="Copy all 5 verdicts as a multi-line block (symbol · module · status · confidence · risk · reason)"
            aria-label="Copy all verdicts"
            className={`flex items-center gap-1 text-[8px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm border transition-colors ${
              copiedAll
                ? "border-[#00FF88]/60 text-[#00FF88] bg-[#00FF88]/10"
                : "border-[#26262B] text-[#8A8A93] hover:text-white hover:border-[#3F3F47] disabled:opacity-30 disabled:cursor-not-allowed"
            }`}
          >
            {copiedAll ? <Check size={10} /> : <Copy size={10} />}
            <span data-testid="forge-strategy-verdicts-copy-all-label">
              {copiedAll ? "Copied" : "Copy all"}
            </span>
          </button>
          <FreshnessIndicator
            fetchedAt={fetchedAt}
            onRefresh={loadVerdicts}
            loading={loading}
            testidPrefix="forge-strategy-verdicts"
            refreshTitle="Re-run all 5 strategy evaluators for this symbol"
          />
        </div>
      </div>

      {err && (
        <InlineError
          testId="forge-strategy-verdicts-error"
          className="mb-3"
          onRetry={loadVerdicts}
          message={err}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {STRATEGY_VERDICT_MODULES.map(m => (
          <StrategyVerdictMiniCard
            key={m.id}
            t={t}
            title={m.title}
            symbol={symbol}
            row={rowsByModule[m.id] || {
              symbol, module_id: m.id, status: loading ? "CONTEXT_MISSING" : "CONTEXT_MISSING",
              confidence_score: 0, reason_codes: loading ? ["LOADING"] : ["NO_ROW_FROM_BACKEND"],
            }}
          />
        ))}
      </div>

      <div className="mt-3 text-[9px] font-mono text-[#5F5F66] italic">
        Same evaluator pipeline as the Strategy Scanner page — pure read-only display, no broker or journal writes.
      </div>
    </div>
  );
}

export default StrategyVerdictsSection;
